import { Feature } from 'ol';
import { LineString } from 'ol/geom';
import {
  createContext, Dispatch, ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo, useState,
} from 'react';
import { Origin } from 'types/app.types';
import MainAppContext from '../MainAppContext';

interface CrossSectionProviderProps {
    children: ReactNode,
}

interface CrossSectionContextValues {
  showCrossSectionModal: boolean,
  setShowCrossSectionModal: Dispatch<SetStateAction<boolean>>,
  segment?: Feature<LineString>,
  setSegment: Dispatch<SetStateAction<Feature<LineString> | undefined>>,
  mousePosition?: number,
  setMousePosition: Dispatch<SetStateAction<number>>,
  lastMousePositionUpdateOrigin?: Origin,
  setLastMousePositionUpdateOrigin: Dispatch<SetStateAction<Origin | undefined>>,
}

const CrossSectionContext = createContext<CrossSectionContextValues>({
  showCrossSectionModal: false,
  setShowCrossSectionModal: () => {},
  segment: undefined,
  setSegment: () => {},
  mousePosition: 0,
  setMousePosition: () => {},
  lastMousePositionUpdateOrigin: undefined,
  setLastMousePositionUpdateOrigin: () => {},
});

function CrossSectionProvider({ children } : CrossSectionProviderProps) {
  const { crossSectionMode } = useContext(MainAppContext);
  const [showCrossSectionModal, setShowCrossSectionModal] = useState(false);
  const [segment, setSegment] = useState<Feature<LineString>>();
  const [mousePosition, setMousePosition] = useState(0);
  const [lastMousePositionUpdateOrigin, setLastMousePositionUpdateOrigin] = useState<Origin>();

  const values = useMemo(() => ({
    showCrossSectionModal,
    setShowCrossSectionModal,
    segment,
    setSegment,
    mousePosition,
    setMousePosition,
    lastMousePositionUpdateOrigin,
    setLastMousePositionUpdateOrigin,
  }), [lastMousePositionUpdateOrigin, mousePosition, segment, showCrossSectionModal]);

  useEffect(() => {
    if (!crossSectionMode) {
      setShowCrossSectionModal(false);
      setSegment(undefined);
    }
  }, [crossSectionMode]);

  return (
    <CrossSectionContext.Provider value={values}>
      {children}
    </CrossSectionContext.Provider>
  );
}

export default CrossSectionContext;
export { CrossSectionProvider };
